/**
 * Product form consolidado — add-to-cart AJAX, erros e sticky ATC.
 * Mantém API pública do tema Ella. Build 2026-08-08.
 */
if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.form = this.querySelector('form');
      this.submitButton = this.form?.querySelector('.add-to-cart-button');
      this.submitButtonText = this.submitButton?.querySelector(
        '.saibai-quick-add__label, .button-text-main, span:not(.loading__spinner)'
      );
      this.cart = document.querySelector('cart-drawer');
      this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
      this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');
      this.hideErrors = this.dataset.hideErrors === 'true';

      if (!this.form || !this.submitButton) return;
      if (this.variantIdInput) this.variantIdInput.disabled = false;
      if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this._onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this._onSubmit);
      this.initAgreement();
      this.initErrorSync();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._onSubmit);
      this.cartErrorUnsubscriber?.();
      this.errorObserver?.disconnect();
    }

    initAgreement() {
      if (!this.checkbox || !this.buyItNowButton) return;
      const sync = () => this.buyItNowButton.classList.toggle(
        'disabled',
        !this.checkbox.checked
      );
      this.checkbox.addEventListener('change', sync);
      sync();
    }

    sectionId() {
      const productInfo = this.closest('product-info');
      return productInfo?.dataset.section || productInfo?.dataset.originalSection;
    }

    stickyButton() {
      const sectionId = this.sectionId();
      return sectionId
        ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
        : null;
    }

    setLoading(loading) {
      [this.submitButton, this.stickyButton()].forEach((button) => {
        if (!button) return;
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      });
      this.submitButton?.toggleAttribute('aria-busy', loading);
      if (loading) this.submitButton?.setAttribute('aria-disabled', 'true');
      else this.submitButton?.removeAttribute('aria-disabled');
    }

    initErrorSync() {
      const sectionId = this.sectionId();
      const main = this.querySelector('.product-form__error-message-wrapper');
      const sticky = sectionId
        ? document.querySelector(
            `sticky-atc[data-sticky-section-id="${sectionId}"] .product-form__error-message-wrapper`
          )
        : null;
      if (!main || !sticky) return;

      const sync = () => {
        const text = main.querySelector('.product-form__error-message')?.textContent?.trim();
        sticky.toggleAttribute('hidden', !text || main.hasAttribute('hidden'));
        const target = sticky.querySelector('.product-form__error-message');
        if (target && text) target.textContent = text;
      };

      sync();
      this.errorObserver = new MutationObserver(sync);
      this.errorObserver.observe(main, {
        attributes: true,
        attributeFilter: ['hidden'],
        childList: true,
        subtree: true,
      });

      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (typeof subscribe === 'function' && events?.cartError) {
        this.cartErrorUnsubscriber = subscribe(events.cartError, sync);
      }
    }

    appendExternalProperties(formData) {
      const scope = this.closest('product-info');
      if (!scope) return;

      scope.querySelectorAll('[name^="properties"]').forEach((field) => {
        if (this.form.contains(field) || !field.name || field.disabled) return;
        if (field.type === 'file') {
          if (field.files?.[0]) formData.append(field.name, field.files[0]);
        } else if (field.value != null && field.value !== '') {
          formData.append(field.name, field.value);
        }
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.form || !this.submitButton || this.submitButton.disabled) return;
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      if (this.checkbox && !this.checkbox.checked) return;

      this.handleErrorMessage();
      this.setLoading(true);

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers ||= {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      this.appendExternalProperties(formData);

      if (this.cart?.getSectionsToRender) {
        const sections = this.cart.getSectionsToRender().map(({ id }) => id).join(',');
        formData.set('sections', sections);
        formData.set('sections_url', location.pathname);
        this.cart.setActiveElement?.(document.activeElement);
      }
      config.body = formData;

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
        const data = await response.json();

        if (data.status || data.errors || data.description) {
          const error = data.description || data.errors || data.message || window.cartStrings?.error;
          const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
          if (typeof publish === 'function' && events?.cartError) {
            publish(events.cartError, {
              source: 'product-form-component',
              productVariantId: formData.get('id'),
              errors: error,
              message: data.message,
            });
          }
          this.handleErrorMessage(error);
          return;
        }

        const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
        if (typeof publish === 'function' && events?.cartUpdate) {
          publish(events.cartUpdate, {
            source: 'product-form-component',
            productVariantId: formData.get('id'),
            cartData: data,
          });
        }

        if (!this.cart) {
          location.href = window.routes?.cart_url || '/cart';
          return;
        }

        const quickAdd = this.closest('quick-add-modal');
        const quickView = this.closest('quick-view-modal');
        if (quickAdd?.hide) {
          document.body.addEventListener('modalClosed', () => {
            this.cart?.renderContents?.(data);
          }, { once: true });
          quickAdd.hide(true);
        } else {
          quickView?.hide?.(true);
          await this.cart.renderContents?.(data);
        }
      } catch (error) {
        console.error('[ProductForm]', error);
        this.handleErrorMessage(window.cartStrings?.error || 'Não foi possível adicionar o produto.');
      } finally {
        this.setLoading(false);
      }
    }

    handleErrorMessage(message = '') {
      if (this.hideErrors) return;
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      const target = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !target) return;
      wrapper.toggleAttribute('hidden', !message);
      target.textContent = message || '';
    }

    toggleSubmitButton(disabled = true, text) {
      if (!this.submitButton) return;
      this.submitButton.disabled = disabled;
      if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      if (!disabled && this.submitButtonText) {
        this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Adicionar';
      }
    }

    get variantIdInput() {
      return this.form?.querySelector('[name="id"]') || null;
    }
  });
}
